import { useGetUserJourney, useSearchUsersByName } from '@actions';
import { LoadingSpinner } from '@components/LoadingSpinner/LoadingSpinner';
import Input from '@components/V4/Inputs/Input';
import Select from '@components/V4/Select/Select';
import { EmptyState, FilterField, SectionHeading } from '@modules/Admin/Reporting/common';
import { UserJourneyHubspotDTO, UserJourneyUserDTO } from '@shared/apis/userJourney.apis';
import { ROLES } from '@shared/constants';
import { UserRole } from '@shared/enums';
import useDebounce from 'apps/agora/src/hooks/useDebounce';
import { mergeClassNames } from 'apps/agora/src/utils/helpers';
import moment from 'moment';
import { useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import JourneyTimeline from './JourneyTimeline';

const ROLE_OPTIONS = [
  { value: UserRole.Student, label: 'Students' },
  { value: UserRole.Mentor, label: 'Mentors' },
];

const HUBSPOT_STATUS_LABELS: Record<UserJourneyHubspotDTO['status'], string> = {
  found: 'Contact found',
  not_found: 'No contact with this email',
  unavailable: 'HubSpot could not be reached',
  disabled: 'HubSpot integration is not configured',
};

const formatDate = (value?: string, withTime = false) =>
  value ? moment(value).format(withTime ? 'DD MMM YYYY, HH:mm' : 'DD MMM YYYY') : '—';

const Fact = ({ label, value }: { label: string; value?: string | number | null }) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-[10px] uppercase tracking-wide text-customGrey">{label}</span>
    <span className="text-sm text-white">{value ?? '—'}</span>
  </div>
);

const Badge = ({ children, tone }: { children: string; tone: 'red' | 'amber' | 'grey' }) => (
  <span
    className={mergeClassNames(
      'rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wide',
      tone === 'red'
        ? 'bg-red-500/15 text-red-300'
        : tone === 'amber'
        ? 'bg-amber-500/15 text-amber-300'
        : 'bg-white/5 text-customGrey'
    )}
  >
    {children}
  </span>
);

const UserSummary = ({ user }: { user: UserJourneyUserDTO }) => (
  <section className="flex flex-col gap-4 rounded-lg bg-surfaceObject p-5">
    <div className="flex flex-wrap items-center gap-3">
      <h2 className="text-lg font-bold text-white">{user.fullName}</h2>
      <span className="text-sm text-customGrey">{user.email}</span>
      {user.isDeactivated ? <Badge tone="red">Deactivated</Badge> : null}
      {user.isTestUser ? <Badge tone="amber">Test user</Badge> : null}
      {user.roles.map((role) => (
        <Badge key={role} tone="grey">
          {ROLES[role as UserRole] ?? `Role ${role}`}
        </Badge>
      ))}
    </div>
    <div className="grid grid-cols-2 gap-4 tablet:grid-cols-4">
      <Fact label="Account created" value={formatDate(user.createdAt)} />
      <Fact label="Last login" value={formatDate(user.lastLogin, true)} />
      <Fact label="Phone" value={user.phoneNumber} />
      <Fact label="Parent email" value={user.parentEmail} />
      <Fact label="Graduation year" value={user.graduationYear} />
      <Fact label="Mentor status" value={user.mentorStatus} />
      <Fact label="Deactivation reason" value={user.deactivationReason} />
      <Fact label="User ID" value={user._id} />
    </div>
  </section>
);

const HubspotSummary = ({ hubspot }: { hubspot: UserJourneyHubspotDTO }) => (
  <section className="flex flex-col gap-4 rounded-lg bg-surfaceObject p-5">
    <div className="flex flex-wrap items-center gap-3">
      <h2 className="text-lg font-bold text-white">HubSpot</h2>
      <Badge tone={hubspot.status === 'found' ? 'grey' : 'amber'}>
        {HUBSPOT_STATUS_LABELS[hubspot.status]}
      </Badge>
      {hubspot.url ? (
        <a
          href={hubspot.url}
          target="_blank"
          rel="noreferrer"
          className="text-xs text-customPrimary underline"
        >
          Open contact in HubSpot
        </a>
      ) : null}
    </div>
    {hubspot.status === 'found' ? (
      <div className="grid grid-cols-2 gap-4 tablet:grid-cols-4">
        <Fact label="Contact created" value={formatDate(hubspot.createdAt)} />
        <Fact label="Lifecycle stage" value={hubspot.lifecycleStage} />
        <Fact label="Lead status" value={hubspot.leadStatus} />
        <Fact label="Owner" value={hubspot.owner} />
        <Fact label="Deals" value={hubspot.counts?.deals ?? 0} />
        <Fact label="Tickets" value={hubspot.counts?.tickets ?? 0} />
        <Fact label="Calls" value={hubspot.counts?.calls ?? 0} />
        <Fact
          label="Emails / notes / meetings"
          value={`${hubspot.counts?.emails ?? 0} / ${hubspot.counts?.notes ?? 0} / ${
            hubspot.counts?.meetings ?? 0
          }`}
        />
      </div>
    ) : hubspot.reason ? (
      <p className="text-xs text-customGrey">{hubspot.reason}</p>
    ) : null}
  </section>
);

const UserJourney = () => {
  const history = useHistory();
  const location = useLocation();
  const selectedUserId = new URLSearchParams(location.search).get('userId') ?? undefined;

  const [role, setRole] = useState<UserRole>(UserRole.Student);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350).trim();

  const { data: matches, isFetching: isSearching } = useSearchUsersByName(
    { name: debouncedSearch, role },
    { enabled: debouncedSearch.length >= 2, keepPreviousData: true }
  );

  const { data: journey, isLoading, isError } = useGetUserJourney(selectedUserId);

  const selectUser = (userId: string) => {
    const params = new URLSearchParams(location.search);
    params.set('userId', userId);
    history.replace({ pathname: location.pathname, search: params.toString() });
    setSearch('');
  };

  const { upcoming, past } = useMemo(() => {
    const now = moment();
    const events = journey?.events ?? [];
    return {
      upcoming: events.filter((event) => moment(event.at).isAfter(now)).reverse(),
      past: events.filter((event) => !moment(event.at).isAfter(now)),
    };
  }, [journey]);

  return (
    <div className="flex flex-col gap-8 p-8 font-raleway text-white">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold">User Journey</h1>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-end gap-3">
          <FilterField width="tablet:w-44">
            <Select
              variant="secondary"
              label="Role"
              value={role}
              options={ROLE_OPTIONS}
              allowClear={false}
              onChange={(value) => setRole(value as UserRole)}
            />
          </FilterField>
          <FilterField width="tablet:w-96">
            <Input
              type="text"
              name="journey-search"
              id="journey-search"
              label="Find user"
              placeholder="Name or email (at least 2 characters)"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </FilterField>
        </div>

        {debouncedSearch.length >= 2 ? (
          <div className="flex max-w-2xl flex-col rounded-lg bg-surfaceObject">
            {isSearching && !matches?.length ? (
              <span className="px-4 py-3 text-xs text-customGrey">Searching…</span>
            ) : !matches?.length ? (
              <span className="px-4 py-3 text-xs text-customGrey">No users match.</span>
            ) : (
              matches.map((user) => (
                <button
                  key={user._id}
                  type="button"
                  className={mergeClassNames(
                    'flex items-center justify-between px-4 py-2 text-left text-sm transition-colors hover:bg-white/5',
                    user._id === selectedUserId ? 'text-customPrimary' : 'text-white'
                  )}
                  onClick={() => selectUser(user._id)}
                >
                  <span>{user.fullName || `${user.firstName ?? ''} ${user.lastName ?? ''}`}</span>
                </button>
              ))
            )}
          </div>
        ) : null}
      </div>

      {!selectedUserId ? (
        <EmptyState>Search for a student or mentor to see their journey.</EmptyState>
      ) : isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <LoadingSpinner />
        </div>
      ) : isError || !journey ? (
        <EmptyState>This user could not be loaded.</EmptyState>
      ) : (
        <>
          <UserSummary user={journey.user} />
          <HubspotSummary hubspot={journey.hubspot} />

          <section className="flex flex-col gap-4">
            <SectionHeading
              title={`Scheduled next (${upcoming.length})`}
              description="Meetings and other items dated in the future, soonest first."
            />
            <JourneyTimeline
              events={upcoming}
              groupBy="day"
              emptyMessage="Nothing scheduled ahead."
            />
          </section>

          <section className="flex flex-col gap-4">
            <SectionHeading
              title={`History (${past.length})`}
              description={`Newest first. Generated ${moment(journey.generatedAt).format(
                'DD MMM YYYY, HH:mm'
              )}.`}
            />
            <JourneyTimeline
              events={past}
              initialHidden={['admin', 'email']}
              emptyMessage="No activity recorded for this user yet."
            />
          </section>
        </>
      )}
    </div>
  );
};

export default UserJourney;
